import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IProjectCreateRequest,
  IPhaseDefinition,
  ProjectType,
  Priority,
  PROJECT_TYPE_LABELS,
  PRIORITY_LABELS,
  PREDEFINED_PHASES,
} from '@/types/project';
import EmployeeSelect from '@/components/ui/EmployeeSelect';

interface ProjectFormProps {
  onSubmit: (data: IProjectCreateRequest) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const ProjectForm: React.FC<ProjectFormProps> = ({ onSubmit, onCancel, isSubmitting }) => {
  const { t } = useTranslation('project');

  const [formData, setFormData] = useState<IProjectCreateRequest>({
    project_name: '',
    project_type: 'internal_rd',
    project_manager: '',
    start_date: new Date().toISOString().split('T')[0],
    target_end_date: '',
    priority: 'medium',
    description: '',
    currency: 'TWD',
    phases: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // For customer-originated: track selected predefined phases
  const [selectedPhases, setSelectedPhases] = useState<Set<string>>(new Set());

  // For customer-originated: custom phase input
  const [customPhaseName, setCustomPhaseName] = useState('');

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.project_name.trim()) {
      newErrors.project_name = t('validation.projectNameRequired');
    }

    if (!formData.project_manager.trim()) {
      newErrors.project_manager = t('validation.projectManagerRequired');
    }

    if (!formData.start_date) {
      newErrors.start_date = t('validation.startDateRequired');
    }

    if (!formData.target_end_date) {
      newErrors.target_end_date = t('validation.targetEndDateRequired');
    }

    if (formData.start_date && formData.target_end_date && formData.start_date > formData.target_end_date) {
      newErrors.target_end_date = t('validation.endDateAfterStart');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field: keyof IProjectCreateRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
    // Reset phases when switching project type
    if (field === 'project_type') {
      setSelectedPhases(new Set());
      setFormData((prev) => ({ ...prev, phases: [] }));
    }
  };

  // Toggle a predefined phase selection
  const togglePhase = (phaseName: string) => {
    const newSelected = new Set(selectedPhases);
    if (newSelected.has(phaseName)) {
      newSelected.delete(phaseName);
    } else {
      newSelected.add(phaseName);
    }
    setSelectedPhases(newSelected);

    // Update formData.phases based on selection
    const phases: IPhaseDefinition[] = PREDEFINED_PHASES.filter((p) =>
      newSelected.has(p.phase_name)
    );
    // Also include any custom phases already added
    const customPhases = formData.phases?.filter(
      (p) => !PREDEFINED_PHASES.some((pp) => pp.phase_name === p.phase_name)
    ) || [];
    setFormData((prev) => ({ ...prev, phases: [...phases, ...customPhases] }));
  };

  // Add a custom phase
  const addCustomPhase = () => {
    const name = customPhaseName.trim();
    if (!name) return;
    // Check for duplicates
    const isDuplicate =
      PREDEFINED_PHASES.some((p) => p.phase_name.toLowerCase() === name.toLowerCase()) ||
      formData.phases?.some((p) => p.phase_name.toLowerCase() === name.toLowerCase());
    if (isDuplicate) {
      setErrors((prev) => ({ ...prev, customPhase: t('form.phaseDuplicate') }));
      return;
    }
    const newPhase: IPhaseDefinition = { phase_name: name, phase_type: 'custom' };
    setFormData((prev) => ({
      ...prev,
      phases: [...(prev.phases || []), newPhase],
    }));
    setCustomPhaseName('');
    setErrors((prev) => ({ ...prev, customPhase: '' }));
  };

  // Remove a custom phase
  const removeCustomPhase = (phaseName: string) => {
    setFormData((prev) => ({
      ...prev,
      phases: prev.phases?.filter((p) => p.phase_name !== phaseName) || [],
    }));
  };

  // Get custom phases (non-predefined)
  const customPhases = formData.phases?.filter(
    (p) => !PREDEFINED_PHASES.some((pp) => pp.phase_name === p.phase_name)
  ) || [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
        </div>

        {/* Modal */}
        <div className="inline-block w-full max-w-xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('createProject')}
            </h3>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            {/* Project Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('form.projectName')} *
              </label>
              <input
                type="text"
                value={formData.project_name}
                onChange={(e) => handleChange('project_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 ${
                  errors.project_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('form.projectNamePlaceholder')}
              />
              {errors.project_name && (
                <p className="mt-1 text-sm text-red-500">{errors.project_name}</p>
              )}
            </div>

            {/* Project Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('form.projectType')} *
              </label>
              <select
                value={formData.project_type}
                onChange={(e) => handleChange('project_type', e.target.value as ProjectType)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
              >
                {Object.entries(PROJECT_TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            {/* Phase Selection - Only for customer-originated projects */}
            {formData.project_type === 'customer_originated' && (
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('form.selectPhases')}
                </label>

                {/* Predefined Phase Checkboxes */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {PREDEFINED_PHASES.map((phase) => (
                    <label
                      key={phase.phase_name}
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPhases.has(phase.phase_name)}
                        onChange={() => togglePhase(phase.phase_name)}
                        className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {phase.phase_name}
                      </span>
                    </label>
                  ))}
                </div>

                {/* Custom Phase Input */}
                <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('form.addCustomPhase')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customPhaseName}
                      onChange={(e) => setCustomPhaseName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomPhase())}
                      placeholder={t('form.customPhasePlaceholder')}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 text-sm"
                    />
                    <button
                      type="button"
                      onClick={addCustomPhase}
                      disabled={!customPhaseName.trim()}
                      className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {t('form.add')}
                    </button>
                  </div>
                  {errors.customPhase && (
                    <p className="mt-1 text-sm text-red-500">{errors.customPhase}</p>
                  )}
                </div>

                {/* Custom Phases List */}
                {customPhases.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {customPhases.map((phase) => (
                      <span
                        key={phase.phase_name}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200 rounded-full text-sm"
                      >
                        {phase.phase_name}
                        <button
                          type="button"
                          onClick={() => removeCustomPhase(phase.phase_name)}
                          className="w-4 h-4 flex items-center justify-center hover:bg-emerald-200 dark:hover:bg-emerald-800 rounded-full"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Selected phases count */}
                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                  {t('form.phasesSelected', { count: (formData.phases?.length || 0) })}
                </p>
              </div>
            )}

            {/* Info for internal R&D projects */}
            {formData.project_type === 'internal_rd' && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {t('form.internalRdPhasesInfo')}
                </p>
              </div>
            )}

            {/* Info for software projects */}
            {formData.project_type === 'software' && (
              <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg p-3">
                <p className="text-sm text-violet-700 dark:text-violet-300">
                  {t('form.softwarePhasesInfo')}
                </p>
              </div>
            )}

            {/* Project Manager */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('form.projectManager')} *
              </label>
              <EmployeeSelect
                value={formData.project_manager}
                onChange={(value) => handleChange('project_manager', value)}
                placeholder={t('form.projectManagerPlaceholder')}
                required
                className={errors.project_manager ? 'border-red-500' : ''}
              />
              {errors.project_manager && (
                <p className="mt-1 text-sm text-red-500">{errors.project_manager}</p>
              )}
            </div>

            {/* Dates Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('form.startDate')} *
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleChange('start_date', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 ${
                    errors.start_date ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.start_date && (
                  <p className="mt-1 text-sm text-red-500">{errors.start_date}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('form.targetEndDate')} *
                </label>
                <input
                  type="date"
                  value={formData.target_end_date}
                  onChange={(e) => handleChange('target_end_date', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 ${
                    errors.target_end_date ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.target_end_date && (
                  <p className="mt-1 text-sm text-red-500">{errors.target_end_date}</p>
                )}
              </div>
            </div>

            {/* Priority & Budget Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('form.priority')}
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleChange('priority', e.target.value as Priority)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                >
                  {Object.entries(PRIORITY_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('form.budget')}
                </label>
                <div className="flex">
                  <input
                    type="number"
                    value={formData.budget || ''}
                    onChange={(e) => handleChange('budget', e.target.value ? Number(e.target.value) : undefined)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                    placeholder="0"
                  />
                  <select
                    value={formData.currency}
                    onChange={(e) => handleChange('currency', e.target.value)}
                    className="px-3 py-2 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-lg bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="TWD">TWD</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('form.description')}
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 resize-none"
                placeholder={t('form.descriptionPlaceholder')}
              />
            </div>
          </form>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center"
            >
              {isSubmitting && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {t('common.create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectForm;
